/**
 * Client mirror of constants/minuteCategoryAwards.js.
 * Display / captions / split editor. Complete awards from the server.
 */

export const MINUTE_CATEGORY_EXTRAS = {
  secondaryExtraPercent: 25,
  tertiaryExtraPercent: 10,
} as const;

export const MINUTE_TYPES = ["theory", "lab", "field", "concentration", "capstone"] as const;
export type MinuteType = (typeof MINUTE_TYPES)[number];
export type MinuteTypeSplitPart = Partial<Record<MinuteType, number>>;
export type MinuteTypeSplits = Partial<Record<"primary" | "secondary" | "tertiary", MinuteTypeSplitPart>>;

/** @deprecated use MinuteTypeSplits */
export type HourSplits = MinuteTypeSplits;
export type HourSplitPart = MinuteTypeSplitPart;

export function extraMinutes(base: number, percent: number): number {
  const n = Number(base) * (Number(percent) / 100);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100) / 100;
}

export function compactSplit(part: MinuteTypeSplitPart | null | undefined): MinuteTypeSplitPart {
  const out: MinuteTypeSplitPart = {};
  if (!part || typeof part !== "object") return out;
  for (const key of MINUTE_TYPES) {
    const n = Number(part[key]);
    if (!Number.isFinite(n) || n <= 0) continue;
    out[key] = Math.round(n * 100) / 100;
  }
  return out;
}

export function splitSum(part: MinuteTypeSplitPart | null | undefined): number {
  const compact = compactSplit(part);
  let sum = 0;
  for (const key of MINUTE_TYPES) sum += Number(compact[key]) || 0;
  return Math.round(sum * 100) / 100;
}

export function defaultSplit(total: number): MinuteTypeSplitPart {
  const n = Number(total) || 0;
  if (n <= 0) return {};
  return { theory: n };
}

export function splitInvalid(
  part: MinuteTypeSplitPart | null | undefined,
  total: number,
  required: boolean,
): boolean {
  if (total <= 0) return false;
  const sum = splitSum(part);
  if (required) return sum <= 0 || Math.abs(sum - total) > 0.009;
  return sum > 0 && Math.abs(sum - total) > 0.009;
}
export function displayedSplit(
  part: MinuteTypeSplitPart | null | undefined,
  total: number,
  required = true,
): MinuteTypeSplitPart {
  if (total <= 0) return {};
  const compact = compactSplit(part);
  if (splitSum(compact) > 0) return compact;
  return required ? defaultSplit(total) : compact;
}
